/**
 * Light, dark, or whatever the machine says.
 *
 * The choice is applied in three places, in this order:
 *
 * 1. `index.html` runs a few lines before the bundle loads, so the first
 *    paint is already the right colour. It reads what this browser remembers.
 * 2. This hook keeps the attribute current while the application runs: it
 *    follows the operating system while the choice is System, and it writes
 *    the person's explicit choice to the server so their next machine agrees.
 * 3. The server's copy arrives with the presentation preferences and corrects
 *    this browser when the two disagree, which happens on a machine the
 *    person has not used before.
 *
 * `data-theme` on the root element is the whole mechanism. Every colour in
 * the interface is a token, and the tokens are defined twice — once on
 * `:root`, once on `:root[data-theme="dark"]`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { api } from "./api.ts";

export type ThemeChoice = "system" | "light" | "dark";

const KEY = "arcgrc.theme";

interface PresentationView {
  readonly theme: ThemeChoice;
  readonly illustrations: string;
}

/** What this browser remembers. */
export function rememberedTheme(): ThemeChoice {
  try {
    const saved = window.localStorage.getItem(KEY);
    return saved === "light" || saved === "dark" ? saved : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/** Write the attribute the tokens key on. */
export function applyTheme(choice: ThemeChoice): void {
  const dark = choice === "dark" || (choice === "system" && systemPrefersDark());
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
}

/**
 * The theme, and the one function that changes it.
 *
 * The person's choice is kept in this browser so the next load paints it
 * immediately, and on the server so another machine agrees.
 */
export function useTheme(): {
  readonly choice: ThemeChoice;
  readonly resolved: "light" | "dark";
  readonly setChoice: (choice: ThemeChoice) => void;
} {
  const client = useQueryClient();
  const [choice, setLocal] = useState<ThemeChoice>(rememberedTheme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  const preferences = useQuery({
    queryKey: ["presentation"],
    queryFn: () => api.get<PresentationView>("/api/presentation"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // The bootstrap in `index.html` does this before the first paint. Doing it
  // again on mount costs nothing and keeps a page from staying light when that
  // file did not run: a page held in the browser cache from before the file
  // existed, or a browser that refused it.
  useEffect(() => {
    applyTheme(rememberedTheme());
  }, []);

  // The server's copy wins on a machine this person has not used before: the
  // browser remembers nothing there, so the remembered value is the default.
  useEffect(() => {
    const fromServer = preferences.data?.theme;
    if (fromServer === undefined) return;
    let remembered: string | null = null;
    try {
      remembered = window.localStorage.getItem(KEY);
    } catch {
      remembered = null;
    }
    if (remembered === null && fromServer !== choice) {
      setLocal(fromServer);
      applyTheme(fromServer);
    }
  }, [preferences.data, choice]);

  // Follow the operating system while the choice is System, and keep
  // following it when it changes under us.
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const onChange = (): void => {
      setSystemDark(media.matches);
      if (rememberedTheme() === "system") applyTheme("system");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const save = useMutation({
    mutationFn: (next: ThemeChoice) => api.put<PresentationView>("/api/presentation", { theme: next }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["presentation"] });
    },
  });

  const setChoice = useCallback(
    (next: ThemeChoice): void => {
      setLocal(next);
      applyTheme(next);
      try {
        // System is the absence of a choice, so it is not remembered as one.
        if (next === "system") window.localStorage.removeItem(KEY);
        else window.localStorage.setItem(KEY, next);
      } catch {
        /* A private window keeps nothing. The session still follows it. */
      }
      save.mutate(next);
    },
    [save],
  );

  return {
    choice,
    resolved: choice === "dark" || (choice === "system" && systemDark) ? "dark" : "light",
    setChoice,
  };
}
